import { useEffect, useMemo, useState } from 'react';
import { Box, HStack, Pressable, VStack, View, ScrollView, Stack } from 'native-base';
import { isEmpty, sortBy, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Text, SearchField, ButtonGroup, CloseIcon } from '@pimm/base';
import { CleaningScheduleTypeEnum, Employee, ScheduledEmployeeDto, ShiftEmployeeDto } from '@pimm/services/lib/sms-workforce';
import { formatTo, formatToTimeOnly } from '@app/utils/date-formatter';
import { formatName } from '@app/utils/string-formatter';
import { ResourceLoader } from '@app/components/shared';
import { CleaningTaskAssignee } from '../reducers';
import { useCleaningAssignments } from '../context';
import { useGetEligibleEmployees } from '../hooks';
import EmployeeSection from './employee-section';

export type EmployeeAssignmentProps = {
  assignee?: CleaningTaskAssignee;
  positionId?: string;
  scheduleDate?: Date;
  scheduleType?: CleaningScheduleTypeEnum;
  taskId: number;
  taskName?: string;
  taskPosition?: string;
  onChange?: (employee?: ShiftEmployeeDto, date?: Date) => void;
  onClose?: () => void;
};

export const EmployeeAssignment = ({
  positionId,
  scheduleDate,
  scheduleType,
  taskId,
  taskName,
  taskPosition,
  ...props
}: EmployeeAssignmentProps) => {
  const { t } = useTranslation();
  const { employees, siteId, week } = useCleaningAssignments();

  const [eligibleEmployees, setEligibleEmployeesParams] = useGetEligibleEmployees();

  const [searchValue, setSearchValue] = useState<string>();
  const [employeeId] = useState<string | undefined>(props.assignee?.employeeId);
  const [employeeTab, setEmployeeTab] = useState<'scheduled' | 'all'>('scheduled');

  // Fetch eligible employees when on scheduled tab
  useEffect(() => {
    if (siteId && scheduleDate) {
      setEligibleEmployeesParams({
        siteId,
        dateTime: scheduleDate,
        positionId,
      });
    }
  }, [siteId, scheduleDate, positionId]);

  const { allEmployees, suggestedEmployees, scheduledEmployees } = useMemo(() => {
    const searchLower = searchValue?.toLowerCase();
    const matchesSearch = (emp: { firstName?: string; lastName?: string }) =>
      !searchLower || formatName(emp?.firstName, emp?.lastName).toLowerCase().includes(searchLower);

    // Process eligible employees
    const { suggested, scheduled } = ((eligibleEmployees.data ?? []) as ScheduledEmployeeDto[]).reduce(
      (acc, emp) => {
        if (!matchesSearch(emp)) return acc;
        acc[emp.isSuggestedMatch ? 'suggested' : 'scheduled'].push(emp);
        return acc;
      },
      { suggested: [] as ScheduledEmployeeDto[], scheduled: [] as ScheduledEmployeeDto[] },
    );

    // Process all employees
    const allEmployees = uniqBy(
      (employees.data ?? []).map(employee => ({ ...employee, employeeId: employee?.id })) as Employee[],
      'employeeId',
    ).filter(matchesSearch);

    return {
      allEmployees: sortBy(allEmployees, emp => formatName(emp?.firstName, emp?.lastName)),
      suggestedEmployees: sortBy(suggested, emp => formatName(emp?.firstName, emp?.lastName)),
      scheduledEmployees: sortBy(scheduled, emp => formatName(emp?.firstName, emp?.lastName)),
    };
  }, [employees.data, eligibleEmployees.data, searchValue]);

  const hasNoEmployees = employeeTab === 'all' ? isEmpty(allEmployees) : isEmpty(suggestedEmployees) && isEmpty(scheduledEmployees);

  const handlePressSave = async (employee: Employee | ScheduledEmployeeDto) => {
    const date = scheduleDate || week.startDate;

    if (employee && props.onChange) {
      const shiftEmployee: ShiftEmployeeDto = {
        id: (employee as ScheduledEmployeeDto).employeeId ?? (employee as Employee).id,
        firstName: employee.firstName,
        lastName: employee.lastName,
      };
      props.onChange(shiftEmployee, date);
    }
  };

  return (
    <View w="full" h="full">
      <HStack alignItems="center" px={4} h="56px" borderBottomWidth={1}>
        <Text size="xl" fontWeight={600} color="gray.900">
          {t('cleaning_task:employee_assignment')}
        </Text>
      </HStack>
      <Stack
        px={{ base: 3, md: 4 }}
        py={{ base: 2, md: 3 }}
        borderBottomWidth={1}
        direction={{ base: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'flex-start', lg: 'center' }}
        space={{ base: 4, lg: 0 }}
      >
        <VStack space={1} flexShrink={1} maxW={{ base: 'full', lg: '60%' }}>
          <Text size={{ base: 'xl', md: '2xl' }} fontWeight={600} color="black" lineHeight="sm">
            {taskName}
          </Text>
          <Text size={{ base: 'sm', md: 'md' }} fontWeight={500} color="gray.600" numberOfLines={2} lineHeight="sm">
            {formatTo(scheduleDate, 'dddd, MMM DD, YYYY')} {t('cleaning_task:at')} {formatToTimeOnly(scheduleDate)} {`· ${taskPosition}`}
          </Text>
        </VStack>

        <Stack
          space={3}
          direction={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'stretch', md: 'center' }}
          justifyContent={{ base: 'flex-start', lg: 'flex-end' }}
          w={{ base: 'full', md: 'auto' }}
        >
          <Box w={{ base: 'full', md: 'auto' }}>
            <ButtonGroup value={employeeTab} onChange={setEmployeeTab}>
              <ButtonGroup.Item value="scheduled" w={{ base: '50%', md: '160px' }}>
                {t('cleaning_task:scheduled_employees')}
              </ButtonGroup.Item>
              <ButtonGroup.Item value="all" w={{ base: '50%', md: '140px' }}>
                {t('cleaning_task:all_employees')}
              </ButtonGroup.Item>
            </ButtonGroup>
          </Box>
          <Box w={{ base: 'full', md: '320px' }} minW={{ lg: '260px' }}>
            <SearchField
              placeholder={t('cleaning_task:search_employee')}
              value={searchValue}
              onChangeText={setSearchValue}
              onClear={() => setSearchValue(undefined)}
            />
          </Box>
        </Stack>
      </Stack>

      <Box flex={1} p={4} position="relative" h="full" bg="gray.25">
        <ResourceLoader
          h="full"
          w="full"
          emptyMessage={employeeTab === 'scheduled' ? t('cleaning_task:no_schedules_available') : t('cleaning_task:no_available_employees')}
          isEmpty={hasNoEmployees}
          isLoading={employeeTab === 'scheduled' ? eligibleEmployees.isLoading : employees.isLoading}
        >
          <ScrollView h="full">
            <VStack space={4}>
              {employeeTab === 'all' ? (
                <EmployeeSection
                  title={t('cleaning_task:all_employees')}
                  subtitle={t('cleaning_task:all_employees_note')}
                  employees={allEmployees}
                  employeeId={employeeId}
                  onSelect={handlePressSave}
                />
              ) : (
                <>
                  {!isEmpty(suggestedEmployees) && (
                    <EmployeeSection
                      title={t('cleaning_task:suggested_employees')}
                      subtitle={t('cleaning_task:suggested_employees_notes')}
                      employees={suggestedEmployees}
                      employeeId={employeeId}
                      onSelect={handlePressSave}
                    />
                  )}
                  {!isEmpty(scheduledEmployees) && (
                    <EmployeeSection
                      title={t('cleaning_task:scheduled_employees')}
                      subtitle={t('cleaning_task:scheduled_employees_notes')}
                      employees={scheduledEmployees}
                      employeeId={employeeId}
                      onSelect={handlePressSave}
                    />
                  )}
                </>
              )}
            </VStack>
          </ScrollView>
        </ResourceLoader>
      </Box>

      {!!props.assignee?.employeeId && (
        <HStack
          px={4}
          py={3}
          borderTopWidth={1}
          borderColor="gray.100"
          bg="gray.25"
          alignItems="center"
          justifyContent="space-between"
          flexShrink={0}
        >
          <Text size="md" color="gray.500">
            {t('cleaning_task:currently_assigned')}{' '}
            <Text size="md" fontWeight={600} color="gray.900">
              {formatName(props.assignee.firstName, props.assignee.lastName)}
            </Text>
          </Text>
          <Pressable
            onPress={() => {
              if (props.onChange) props.onChange();
              if (props.onClose) props.onClose();
            }}
          >
            <HStack px={3} py={3} space={1.5} alignItems="center" bg="error.50" borderWidth={1} borderColor="error.200" rounded="md">
              <CloseIcon size="md" color="error.600" />
              <Text size="md" fontWeight={600} color="error.700" textTransform="capitalize">
                {t('cleaning_task:remove_employee')}
              </Text>
            </HStack>
          </Pressable>
        </HStack>
      )}
    </View>
  );
};
